import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Tag, Search, ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PlaceAutocomplete from "@/components/PlaceAutocomplete";
import { useGooglePlaces, type Prediction } from "@/hooks/useGooglePlaces";
import { motion } from "framer-motion";

const BookingWidget = () => {
  const navigate = useNavigate();
  const [routeType, setRouteType] = useState<"one-way" | "round-trip">("one-way");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [fromPlaceId, setFromPlaceId] = useState("");
  const [toPlaceId, setToPlaceId] = useState("");
  const [passengers, setPassengers] = useState("2");
  const [promoCode, setPromoCode] = useState("");

  const fromPlaces = useGooglePlaces();
  const toPlaces = useGooglePlaces();

  const isAirportRoute = fromPlaceId.startsWith("db-airport-") || toPlaceId.startsWith("db-airport-");
  const maxPassengers = isAirportRoute ? 50 : 10;

  useEffect(() => {
    if (parseInt(passengers) > maxPassengers) {
      setPassengers(String(maxPassengers));
    }
  }, [maxPassengers]);

  const handleFromSelect = (prediction: Prediction) => {
    setFrom(prediction.description);
    setFromPlaceId(prediction.place_id);
    fromPlaces.setPredictions([]);
  };

  const handleToSelect = (prediction: Prediction) => {
    setTo(prediction.description);
    setToPlaceId(prediction.place_id);
    toPlaces.setPredictions([]);
  };

  const handleSearch = () => {
    navigate("/vehicles", {
      state: {
        from, to, fromPlaceId, toPlaceId, routeType,
        passengers: parseInt(passengers),
        promoCode,
      },
    });
  };

  return (
    <div className="w-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-lg p-6 md:p-8 shadow-lg border border-border/50"
      >
        {/* Header */}
        <h2 className="font-display text-xl md:text-2xl font-bold text-foreground mb-1">
          Find Your Next Destination!
        </h2>
        <p className="text-muted-foreground text-sm mb-6">
          Enter your trip details below
        </p>

        {/* Route Type Toggle */}
        <div className="flex mb-6 bg-muted rounded-lg p-1">
          <button
            onClick={() => setRouteType("one-way")}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all ${
              routeType === "one-way"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            One Way
          </button>
          <button
            onClick={() => setRouteType("round-trip")}
            className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all ${
              routeType === "round-trip"
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Round Trip
          </button>
        </div>

        {/* Route Inputs */}
        <div className="space-y-3 mb-4">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center z-10">
              <MapPin className="w-4 h-4 text-primary" />
            </div>
            <PlaceAutocomplete
              placeholder="Pick-up: Airport, Hotel or Address"
              value={from}
              onChange={setFrom}
              onSelect={handleFromSelect}
              predictions={fromPlaces.predictions}
              loading={fromPlaces.loading}
              onSearch={fromPlaces.search}
              iconColor="hidden"
            />
          </div>

          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-md bg-accent/10 flex items-center justify-center z-10">
              <MapPin className="w-4 h-4 text-accent" />
            </div>
            <PlaceAutocomplete
              placeholder="Drop-off: Hotel, Address or Airport"
              value={to}
              onChange={setTo}
              onSelect={handleToSelect}
              predictions={toPlaces.predictions}
              loading={toPlaces.loading}
              onSearch={toPlaces.search}
              iconColor="hidden"
            />
          </div>
        </div>

        {/* Passengers & Promo */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center z-10">
              <Users className="w-4 h-4 text-primary" />
            </div>
            <Select value={passengers} onValueChange={setPassengers}>
              <SelectTrigger className="pl-13 h-12 bg-white border-border rounded-lg text-foreground text-sm font-medium hover:border-primary/40 transition-colors">
                <SelectValue placeholder="Passengers" />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: maxPassengers }, (_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>
                    {i + 1} {i === 0 ? "Passenger" : "Passengers"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-md bg-secondary/10 flex items-center justify-center z-10">
              <Tag className="w-4 h-4 text-secondary" />
            </div>
            <input
              placeholder="Promo Code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              className="flex h-12 w-full rounded-lg border border-border bg-white px-3 pl-13 py-2 text-sm font-medium text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0 hover:border-primary/40 transition-colors"
            />
          </div>
        </div>

        {/* Search Button */}
        <Button
          onClick={handleSearch}
          size="lg"
          className="w-full h-12 text-sm font-bold gap-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all"
        >
          <Search className="w-4 h-4" />
          Search Available Vehicles
          <ArrowRight className="w-4 h-4" />
        </Button>
      </motion.div>
    </div>
  );
};

export default BookingWidget;
